import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Notification from './Notification';
import CookiesNotification from './CookiesNotification';
import Navigation from './Navigation';
import Search from './Search';
import acuityAnalytics from './acuityAnalytics';
import Cart from '../Selection/Cart';
import Select from '../Elements/Select/Select';
import { translate, prefix } from '../../utils/locale';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset } from '../../utils/dom';
import { removeNotification, openCart, openMenu, clearFilter, resetFilter, changeLanguage, goToPage, changeDevice, setIframeHeight, cookieConsent } from '../../actions/environment';
import { clientInit, changeCountry, changeAddressInput } from '../../actions/checkout';
import s from './Header.scss';

let stickyOffset = 46; // tablet, desktop 52
const breakPoints = [900, 600];
const media = { w_600: 'mobile', w_900: 'tablet', w_1200: 'desktop' };

class Header extends Component {
	constructor(props) {
		super(props);

		this.state = {
			location: false,
			grid: false,
			menuPopupHeight: 'auto',
			menuOffset: null,
		};

		this.headerElement = React.createRef();
		this.linksElement = React.createRef();
		this.cartElement = React.createRef();
		this.languageCountryElement = React.createRef();
	}

	componentDidMount() {
		if (typeof window !== 'undefined') {
			const $html = document.querySelector('html');
			let currentWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
			let device = media[`w_${currentWidth}`];

			this.props.clientInit();
			this.props.changeDevice(device);
			if (device === 'tablet' || device === 'desktop') stickyOffset = 52;
			else stickyOffset = 46;

			// Wait for page to be rendered with all relevant elements.
			const initiateSiteListeners = () => {
				if (!this.headerElement || !this.linksElement || !this.cartElement) {
					setTimeout(initiateSiteListeners, 200);
				}

				const startTop = window.pageYOffset || document.documentElement.scrollTop;
				const menuOffset = offset(this.headerElement.current).top + stickyOffset;
				const menuOffsetWindowTop = startTop > offset(this.headerElement.current).top ? 0 : offset(this.headerElement.current).top;
				const bottomFunctions = window.innerWidth < 600 ? 166 : 0;

				let isScrolled = false;
				let headerIsFixed = false;
				let isScrollingUp = false;
				let lastScrolledDown = 0;
				let lastScrolledUp = 1e5;

				if (this.state.menuOffset === null) {
					this.setState({
						menuOffset,
						menuPopupHeight: window.innerHeight - menuOffsetWindowTop - bottomFunctions,
					});
				}

				const resizeEvent = () => {
					const updatedWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
					this.setState({ menuOffset: offset(this.headerElement.current).top + stickyOffset });

					// Switch between mobile, table and desktop.
					if (currentWidth !== updatedWidth) {
						device = media[`w_${updatedWidth}`];
						this.props.changeDevice(device);
						if (device === 'tablet' || device === 'desktop') stickyOffset = 52;
						else stickyOffset = 46;
						currentWidth = updatedWidth;
					}
				};

				addEvent(window, 'resize.logoHeader', () => {
					window.requestAnimationFrame(resizeEvent);
				});

				addEvent(window, 'message.acuityListener', (m) => {
					if (typeof m === 'string' && m.indexOf('sizing') === 0) {
						const height = parseInt(m.split(':').slice(-1)[0], 10);
						this.props.setIframeHeight(height);
					}
				});

				addEvent(window, 'message.acuityConversionListener', (m) => {
					if (m.origin === 'https://conversion-tracking-sandbox.acuityinnovation.com') {
						// Conversion data sent from custom conversion acuity plugin.
						acuityAnalytics(m.data);
					}
				});

				const scrollEvent = () => {
					const top = window.pageYOffset || document.documentElement.scrollTop;
					if (this.state.menuOffset === null) {
						this.setState({
							menuOffset: offset(this.headerElement.current).top + stickyOffset,
						});
					}

					// make logo sticky when level with header.
					if (!headerIsFixed && top >= this.state.menuOffset) {
						headerIsFixed = true;
						addClass($html, 'u_headerIsFixed');
						this.setState({ location: false });
						this.props.openCart(false);
						this.props.openMenu(false);
					} else if (headerIsFixed && top < this.state.menuOffset - stickyOffset) {
						headerIsFixed = false;
						removeClass($html, 'u_headerIsFixed');
					}

					if (top > 0 && !isScrolled) {
						addClass($html, 'u_pageIsScrolled');
						isScrolled = true;
					} else if (top === 0) {
						removeClass($html, 'u_pageIsScrolled');
						removeClass($html, 'u_headerIsFixed');
						removeClass($html, 'u_pageIsScrollingUp');
						isScrolled = false;
						headerIsFixed = false;
					}

					if (top > lastScrolledDown) lastScrolledDown = top;
					if ((top < lastScrolledUp && isScrollingUp) || !isScrollingUp) lastScrolledUp = top;

					// console.log('DEBUG: lastScrolledDown, lastScrolledUp, top', lastScrolledDown, lastScrolledUp, top, isScrollingUp);

					if (lastScrolledDown - top > 50 && !isScrollingUp) {
						addClass($html, 'u_pageIsScrollingUp');
						isScrollingUp = true;
					}
					if (top - lastScrolledUp > 50 && isScrollingUp) {
						removeClass($html, 'u_pageIsScrollingUp');
						this.setState({ location: false });
						this.props.openCart(false);
						this.props.openMenu(false);
						lastScrolledDown = top;
						isScrollingUp = false;
					}
				};

				addEvent(window, 'scroll.logoHeader', () => {
					window.requestAnimationFrame(scrollEvent);
				});

				addEvent(document.querySelector('body'), 'click.cartClose', (e) => {
					if (this.cartElement.current && !this.cartElement.current.contains(e.target) && !this.headerElement.current.contains(e.target) && this.props.properties.openCart === 1) {
						e.preventDefault();
						e.stopPropagation();
						this.props.openCart(false);
					}
					if (this.languageCountryElement.current && !this.languageCountryElement.current.contains(e.target) && this.state.location) {
						e.preventDefault();
						e.stopPropagation();
						this.setState({ location: false });
					}
				});
			};

			initiateSiteListeners();

			if ('ontouchstart' in document.documentElement) {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
			}

			if (hasClass($html, 'no-touchevents')) {
				addEvent(window, 'touchstart.touchTest', () => {
					removeClass($html, 'no-touchevents');
					addClass($html, 'touchevents');
					removeEvent(window, 'touchstart.touchTest');
				});
			}

			addEvent(document.querySelector('body'), 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');

				if ($link && this.props.checkout.klarnaUnloaded && $link.getAttribute('href').indexOf('#') !== 0) {
					e.preventDefault();
					e.stopPropagation();
					window.location = $link.href;
					// Force full page reload to clear Klarna on next page click.
				} else if ($link && hasClass($link, 'linkCapture')) {
					e.preventDefault();
					this.props.goToPage($link.getAttribute('href'));
				}
			});

			addEvent(window, 'keydown.general', (e) => {
				if (e.key === 'Escape') {
					if (this.props.properties.openCart === 1) this.props.openCart(false);
					if (this.state.location === true) this.setState({ location: false });
					if (this.state.grid) this.setState({ grid: false });
				} else if (e.shiftKey && e.ctrlKey && e.key === 'A') {
					// ctrl + shift + a = Toggle grid
					this.setState((prevState) => ({ grid: !prevState.grid }));
				}
			});
		}
	}

	componentWillUnmount() {
		removeEvent(document.querySelector('body'), 'click.cartClose');
		removeEvent(window, 'keydown.general');
		removeEvent(window, 'scroll.logoHeader');
		removeEvent(window, 'resize.logoHeader');
		removeEvent(window, 'message.acuityListener');
		removeEvent(window, 'message.acuityConversionListener');
	}

	openLocation = (e) => {
		e.preventDefault();

		this.setState((prevState) => ({ location: !prevState.location }));
	}

	changeLanguage = (e) => {
		const changeTo = e.currentTarget.dataset.language;
		this.props.changeLanguage(changeTo);
		this.setState({
			location: false,
		});
	}

	openCartHandler = (e) => {
		if (this.props.properties.openCart === 0) {
			e.preventDefault();

			this.setState(() => {
				const top = window.pageYOffset || document.documentElement.scrollTop;
				const menuOffsetWindowTop = top > offset(this.headerElement.current).top ? 0 : offset(this.headerElement.current).top;

				return { menuPopupHeight: window.innerHeight - menuOffsetWindowTop };
			});

			if (this.props.navigation.open) this.props.openMenu();
			this.props.openCart();
		} else if (this.props.checkout.klarnaUnloaded) {
			e.preventDefault();
			window.location = `${window.location.origin}/${this.props.setup.language}/selection`;
		}
	}

	notificationRemovedHandler = () => {
		setTimeout(() => {
			const menuOffset = offset(this.headerElement.current).top + stickyOffset;

			this.setState({
				menuOffset,
			});
		}, 500);
	}

	openMobileMenuHandler = (e) => {
		e.preventDefault();

		this.setState(() => {
			const top = window.pageYOffset || document.documentElement.scrollTop;
			const menuOffsetWindowTop = top > offset(this.headerElement.current).top ? 0 : offset(this.headerElement.current).top;
			const bottomFunctions = window.innerWidth < 600 ? 166 : 0;

			return { menuPopupHeight: window.innerHeight - menuOffsetWindowTop - bottomFunctions };
		});

		if (this.props.properties.openCart === 1) this.props.openCart(false);
		this.props.openMenu();
	}

	resetFilterHandler = () => {
		this.props.resetFilter();
	}

	render() {
		const { properties, navigation, setup, header, checkout, pagePaths, location } = this.props;
		const { grid } = this.state;
		const { items = [] } = checkout.selection;
		if (properties.siteInitiated === false || properties.siteClosed === true) return null;

		const openCartStates = [null, 'is-showingCart__entering', 'is-showingCart__exiting'];
		const headerClasses = [s.header, openCartStates[properties.openCart]];

		const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: `${setup.countries[country].name}${(!setup.countries[country].shipTo ? ' *' : '')}`, disabled: !setup.countries[country].shipTo }] : [...acc]), []);
		const onCountryChange = (id, value) => {
			this.props.changeCountry(value);
			this.props.changeAddressInput('address', 'country', value);
			this.props.changeAddressInput('shippingAddress', 'country', value);
			this.setState({
				location: false,
			});
		};

		let searchReturn = prefix(setup, '');
		if (location.prev && location.prev.type !== 'SEARCH' && location.prev.pathname) searchReturn = location.prev.pathname;

		return (
			<>
				<Notification type="campaign" content={header.campaign} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				<CookiesNotification type="cookies" content={header.cookies} setup={setup} cookieConsent={this.props.cookieConsent} />
				<Notification type="popup" content={header.popup} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} />
				{ setup.unableToSendToCountry ? <Notification type="error" content={[{ name: 'unableToSendToCountry', text: setup.statics.unableToSendToCountry }]} setup={setup} /> : null }
				<header className={headerClasses.join(' ')} ref={this.headerElement}>
					<div className={s.header_fixedContainer}>
						<div className={s.header_links} ref={this.linksElement}>
							<Link className={s.header_logo} to={prefix(setup)}>
								<svg xmlns="http://www.w3.org/2000/svg" width="200" height="32" viewBox="0 0 200 32">
									<path d="M26.6348039 25.1696078L26.6348039 20.3117647C26.6348039 19.5578431 26.5093137 19.4318627 25.8808824 19.4318627L21.5254902 19.4318627C21.0230392 19.4318627 20.8970588 19.5578431 20.8970588 20.0602941 20.8970588 20.6882353 21.1071078 21.0232843 21.7764706 21.4421569 22.5303922 21.9446078 22.7406863 22.4892157 22.7406863 24.1639706 22.7406863 26.132598 22.4470588 27.1379902 21.5681373 27.9333333 20.5627451 28.9384804 18.5936275 29.4409314 16.039951 29.4409314 12.5213235 29.4409314 9.96715686 28.4779412 7.99852941 26.4254902 5.65367647 24.0384804 4.39730392 20.2691176 4.39730392 15.6208333 4.39730392 10.8887255 5.69509804 7.32941176 8.29166667 4.73308824 10.2178922 2.80661765 12.5639706 1.75906863 15.0764706 1.75906863 18.4681373 1.75906863 20.8970588 3.3504902 22.6980392 6.65931373 23.3686275 7.91519608 23.8289216 8.33431373 24.4568627 8.33431373 24.8338235 8.33431373 25.0441176 8.16691176 25.0441176 7.87328431L25.0441176 1.75906863C25.0441176 1.42401961 24.9593137 1.38210784 24.5830882 1.38210784 24.289951 1.38210784 24.0384804 1.42401961 23.5360294 1.59215686 23.075 1.75906863 22.8235294 1.84313725 22.572549 1.84313725 22.1955882 1.84313725 21.6936275 1.6754902 20.8556373 1.29852941 18.6776961.377205882 16.9186275 0 14.8247549 0 10.7210784 0 7.03578431 1.63357843 4.10441176 4.81642157 1.33995098 7.83186275 0 11.5171569 0 16.0818627 0 20.6046569 1.33995098 24.2480392 3.97892157 26.9284314 6.82671569 29.8598039 10.8044118 31.2419118 16.1232843 31.2419118 20.1436275 31.2419118 23.8710784 30.6132353 28.5612745 29.0223039L28.5612745 28.0588235C26.802451 27.1794118 26.6348039 26.9284314 26.6348039 25.1696078M37.4990196 29.5247549C34.7352941 29.5247549 32.9343137 26.9284314 32.9343137 22.8240196 32.9343137 18.4688725 34.6512255 15.9558824 37.5404412 15.9558824 40.4303922 15.9558824 42.2313725 18.5529412 42.2313725 22.6985294 42.2313725 27.0120098 40.5139706 29.5247549 37.4990196 29.5247549M37.5404412 14.4063725C32.9764706 14.4063725 29.4159314 18.0916667 29.4159314 22.782598 29.4159314 27.4730392 33.0178922 31.1161765 37.6659314 31.1161765 42.1058824 31.1161765 45.7487745 27.347549 45.7487745 22.782598 45.7487745 18.0916667 42.1058824 14.4063725 37.5404412 14.4063725M79.1232843 29.6085784C78.2852941 29.2730392 78.0343137 28.8125 78.0343137 27.5147059L78.0343137 14.6997549 73.260049 14.6997549C72.8409314 14.6997549 72.5480392 14.9507353 72.5480392 15.3276961 72.5480392 15.7468137 72.757598 15.9142157 73.3433824 16.0397059 74.2235294 16.2071078 74.6833333 16.7517157 74.6833333 17.5897059L74.6833333 27.807598C74.6833333 28.4355392 74.6004902 28.6458333 74.264951 29.0220588 74.0139706 29.2316176 73.8879902 29.3156863 73.5110294 29.4411765 72.8830882 29.65 72.6740196 29.9014706 72.6740196 30.3205882 72.6740196 30.6556373 72.7995098 30.8230392 73.0504902 30.8230392L79.5843137 30.8230392C79.7512255 30.8230392 79.8352941 30.6970588 79.8352941 30.4460784 79.8352941 29.9855392 79.6671569 29.8181373 79.1232843 29.6085784M76.3169118 5.6120098C77.4485294 5.6120098 78.4107843 4.64926471 78.4107843 3.51813725 78.4107843 2.34583333 77.4485294 1.42426471 76.2754902 1.42426471 75.1443627 1.42426471 74.2235294 2.3872549 74.2235294 3.51813725 74.2235294 4.64926471 75.1443627 5.6120098 76.3169118 5.6120098M94.6563725 27.5144608C94.4889706 27.5144608 94.1953431 27.6821078 93.7764706 27.9754902 92.5205882 28.8129902 91.347549 29.189951 90.0916667 29.189951 88.6257353 29.189951 87.3272059 28.6455882 86.2801471 27.5985294 85.0245098 26.3421569 84.4379902 24.5830882 84.4379902 22.1544118 84.4379902 19.8509804 84.9823529 18.1754902 86.0713235 17.0865196 86.9088235 16.2490196 88.1651961 15.7465686 89.3382353 15.7465686 89.9669118 15.7465686 90.4495098 15.9098039 90.8223039 16.2647059 90.6276961 16.5845588 90.510049 16.9556373 90.510049 17.3504902 90.510049 18.4816176 91.4316176 19.4443627 92.6039216 19.4443627 93.4894608 19.4443627 94.2644608 18.8514706 94.5622549 18.052451 94.5970588 17.9730392 94.6215686 17.8867647 94.6438725 17.7990196 94.6463235 17.7865196 94.6517157 17.775 94.6546569 17.7617647 94.6811275 17.6411765 94.6977941 17.514951 94.6977941 17.379902 94.6977941 17.3732843 94.6968137 17.367402 94.6968137 17.3607843 94.6968137 17.3578431 94.6973039 17.3541667 94.6973039 17.3504902 94.6973039 17.3132353 94.6889706 17.2796569 94.6870098 17.2436275 94.6816176 17.1747549 94.6781863 17.1061275 94.6661765 17.0389706 94.5916667 16.5448529 94.3468137 16.1144608 93.9894608 15.7992647 93.1009804 14.8713235 91.470098 14.2392157 89.6311275 14.2392157 84.9397059 14.2392157 80.8779412 18.2595588 80.8779412 22.8242647 80.8779412 27.5985294 84.3125 31.1164216 89.0446078 31.1164216 91.347549 31.1164216 93.5254902 30.3203431 94.8659314 29.022549 95.1583333 28.7289216 95.2428922 28.5193627 95.2428922 28.1845588 95.2428922 27.807598 95.0335784 27.5144608 94.6563725 27.5144608M112.664216 29.4828431C111.868873 29.2321078 111.743382 28.8963235 111.743382 27.5144608L111.743382 21.5676471C111.743382 18.8458333 111.240196 17.1703431 110.067892 15.9558824 109.06299 14.9931373 107.680882 14.4485294 106.173039 14.4485294 104.329902 14.4485294 102.529657 15.1186275 100.854167 16.5004902L100.854167.628676471 96.8338235.628676471C96.122549.628676471 95.7034314.921568627 95.7034314 1.38210784 95.7034314 1.71764706 95.8294118 1.88504902 96.289951 2.17818627 97.2115196 2.76470588 97.5044118 3.3504902 97.5044118 4.52352941L97.5044118 27.8073529C97.5044118 29.1058824 97.3784314 29.3573529 96.5828431 29.5669118 96.079902 29.7338235 95.9129902 29.8598039 95.9129902 30.2789216 95.9129902 30.6558824 96.0384804 30.8227941 96.2480392 30.8227941L102.278676 30.8227941C102.446078 30.8227941 102.571569 30.5718137 102.571569 30.3625 102.571569 29.9433824 102.362255 29.7338235 101.818382 29.5669118 101.482598 29.4409314 101.398775 29.3987745 101.231863 29.2735294 100.938725 28.9384804 100.854167 28.6455882 100.854167 27.9754902L100.854167 18.3433824C102.111275 17.0870098 103.57598 16.4169118 105.084069 16.4169118 107.095098 16.4169118 108.392402 17.9664216 108.392402 20.4372549L108.392402 28.0176471C108.392402 28.5615196 108.267402 28.8963235 107.97451 29.2321078 107.764461 29.3987745 107.680882 29.4409314 107.346078 29.525 106.759559 29.692402 106.591912 29.9019608 106.591912 30.3203431 106.591912 30.6132353 106.717402 30.8227941 106.926961 30.8227941L113.083333 30.8227941C113.251225 30.8227941 113.334069 30.5718137 113.334069 30.2370098 113.334069 29.8598039 113.166667 29.6504902 112.664216 29.4828431M33.942402 11.9080882C35.0732843 11.9080882 36.0362745 10.9453431 36.0362745 9.81421569 36.0362745 8.64117647 35.0732843 7.72034314 33.9002451 7.72034314 32.7698529 7.72034314 31.8490196 8.68308824 31.8490196 9.81421569 31.8490196 10.9453431 32.7698529 11.9080882 33.942402 11.9080882M40.9223039 11.9080882C42.0531863 11.9080882 43.0161765 10.9453431 43.0161765 9.81421569 43.0161765 8.64117647 42.0531863 7.72034314 40.8808824 7.72034314 39.7497549 7.72034314 38.8289216 8.68308824 38.8289216 9.81421569 38.8289216 10.9453431 39.7497549 11.9080882 40.9223039 11.9080882M68.7860294 12.8495098C68.3561275 12.8495098 67.957598 12.989951 67.627451 13.2247549 66.2154412 13.8088235 64.8713235 15.664951 63.7781863 18.6365196L63.6953431 18.6365196 63.6953431 18.0916667 63.6526961 17.547549C63.5691176 15.7887255 63.5272059 15.2862745 63.4017157 14.6997549L58.7948529 14.6997549C58.627451 14.6997549 58.4178922 14.9507353 58.4178922 15.2443627 58.4178922 15.6213235 58.627451 15.8720588 59.2968137 16.1237745 59.7164216 16.2492647 59.8419118 16.3333333 60.0933824 16.5840686 60.5539216 17.0446078 60.6372549 17.4637255 60.6372549 18.4686275L60.6372549 28.0171569C60.6372549 28.645098 60.5953431 28.8965686 60.4284314 29.0642157 60.2188725 29.2730392 60.0933824 29.3156863 59.7164216 29.3990196 59.129902 29.5245098 58.9203431 29.7340686 58.9203431 30.1531863 58.9203431 30.4879902 59.129902 30.8230392 59.3394608 30.8230392L65.5377451 30.8230392C65.8306373 30.8230392 65.9139706 30.6556373 65.9139706 30.2786765 65.9139706 29.8181373 65.6632353 29.5666667 64.9931373 29.3990196 64.1556373 29.1897059 63.9882353 28.8125 63.9882353 27.4313725L63.9882353 23.1595588C63.9882353 21.6933824 64.6995098 19.1806373 65.5791667 17.6311275 66.0742647 16.7276961 66.5294118 16.1561275 66.9723039 15.8877451 67.3164216 16.5595588 68.0061275 17.0375 68.8279412 17.0375 69.9590686 17.0375 70.922549 16.0745098 70.922549 14.9433824 70.922549 13.7710784 69.9590686 12.8495098 68.7860294 12.8495098M55.802451 16.3330882C56.4303922 16.3330882 56.5977941 16.1656863 56.5977941 15.4955882 56.5977941 14.8257353 56.514951 14.6997549 56.1372549 14.6997549L51.9218137 14.6997549 51.9218137 9.16102941 48.5720588 9.16102941 48.5720588 14.6997549 46.7357843 14.6997549C46.5683824 14.6997549 46.3593137 14.9512255 46.3593137 15.2441176 46.3593137 15.6210784 46.5683824 15.8720588 47.2382353 16.1235294 47.6578431 16.2490196 47.7833333 16.3330882 48.0343137 16.5840686 48.4178922 16.9676471 48.5384804 17.3272059 48.5686275 18.0156863L48.5720588 18.0156863 48.5720588 27.8176471 48.5818627 27.8176471C48.6808824 29.8215686 50.1232843 31.0323529 52.4664216 31.0323529 53.9330882 31.0323529 55.3563725 30.5720588 56.1938725 29.7759804 56.4872549 29.525 56.5294118 29.3568627 56.5708333 28.8129902 56.5294118 28.2686275 56.4872549 28.1012255 56.2779412 28.1012255 56.152451 28.1012255 55.9007353 28.1845588 55.5237745 28.3941176 54.7284314 28.8544118 54.1838235 29.022549 53.6394608 29.022549 52.592402 29.022549 51.9218137 28.1845588 51.9218137 26.7612745L51.9218137 16.3330882M134.161765 8.12377451C134.161765 6.28161765 132.947549 4.98357843 131.230637 4.98357843 129.555637 4.98357843 128.383333 6.15612745 128.383333 7.91446078 128.383333 9.2122549 128.969118 10.3012255 130.853431 12.5205882 133.030882 11.3897059 134.161765 9.88284314 134.161765 8.12377451L134.161765 8.12377451zM135.459804 25.8781863C133.030882 22.0259804 131.355882 19.4718137 129.848529 17.4198529 129.346814 16.7497549 129.095098 16.414951 128.508578 15.6615196 126.582843 17.0428922 125.702941 18.6343137 125.702941 20.7281863 125.702941 25.0828431 128.467402 28.9772059 131.606863 28.9772059 133.073529 28.9772059 134.203186 28.3485294 135.878186 26.5901961L135.711275 26.2551471 135.459804 25.8781863zM144.253186 29.4796569L144.629657 29.4796569C145.090686 29.4796569 145.216176 29.6051471 145.216176 30.2335784 145.216176 30.8615196 144.295343 31.1965686 142.787745 31.1965686 140.27549 31.1965686 138.642647 30.275 136.841667 27.8882353 134.873775 30.1495098 132.779902 31.1965686 130.35098 31.1965686 125.954412 31.1965686 122.186275 27.2595588 122.186275 22.6120098 122.186275 19.220098 124.153431 16.2473039 127.629167 14.4044118 125.995833 12.060049 125.493873 10.8458333 125.493873 9.08676471 125.493873 5.77916667 127.838725 3.47573529 131.189216 3.47573529 134.412745 3.47573529 136.715686 5.52745098 136.715686 8.29142157 136.715686 10.4686275 135.585294 11.7666667 131.858578 13.8183824 132.571078 14.6980392 132.86348 15.075 133.366422 15.7870098 134.580392 17.3784314 135.878186 19.3041667 137.803922 22.2769608L138.391176 23.1985294C139.856618 21.1465686 140.86152 18.5921569 140.86152 16.917402 140.86152 16.2053922 140.568382 15.8284314 139.856618 15.4933824 139.4375 15.3259804 139.31201 15.1583333 139.31201 14.8654412 139.31201 14.3629902 139.479412 14.1955882 139.939951 14.1955882L144.546814 14.1955882C145.258088 14.1955882 145.342157 14.2796569 145.342157 14.8240196 145.342157 15.242402 145.216176 15.410049 144.713725 15.7029412 144.295343 15.9125 144.044363 16.1218137 143.751225 16.414951 143.122794 17.1681373 142.745833 17.8387255 142.033824 19.5551471 141.196324 21.4816176 140.40098 22.9889706 139.31201 24.7058824L139.814461 25.3757353C139.981863 25.6693627 140.15 25.8781863 140.232843 26.0036765 142.117402 28.8509804 142.871324 29.4796569 144.169118 29.4796569L144.253186 29.4796569zM191.678431 29.5213235C188.914706 29.5213235 187.113725 26.925 187.113725 22.8210784 187.113725 18.4664216 188.831127 15.9541667 191.719853 15.9541667 194.609314 15.9541667 196.410294 18.5504902 196.410294 22.6955882 196.410294 27.0090686 194.692647 29.5213235 191.678431 29.5213235M191.719853 14.4046569C187.156373 14.4046569 183.596569 18.089951 183.596569 22.7796569 183.596569 27.4693627 187.197794 31.1120098 191.845343 31.1120098 196.284804 31.1120098 199.926961 27.3438725 199.926961 22.7796569 199.926961 18.089951 196.284804 14.4046569 191.719853 14.4046569M180.087255 7.29852941C180.087255 7.84583333 179.907843 8.09803922 179.460539 8.09803922 178.968137 8.09803922 178.699265 7.80343137 178.251471 6.87818627 177.714706 5.82598039 177.31201 5.2370098 176.506373 4.4379902 174.581863 2.75465686 172.568382 1.95563725 170.016912 1.95563725 163.885294 1.95563725 159.588725 7.76127451 159.588725 15.9237745 159.588725 24.0436275 163.930392 29.4284314 170.464706 29.4284314 173.015441 29.4284314 175.656373 28.5870098 177.357108 27.1990196 178.207108 26.5681373 178.789461 25.7683824 179.371078 24.632598 179.863235 23.664951 180.17598 23.3281863 180.62451 23.3281863 181.116176 23.3281863 181.295588 23.4122549 181.295588 23.664951L181.295588 28.5034314C181.295588 28.8823529 181.161275 29.0497549 180.892892 29.0497549 180.802941 29.0497549 180.668873 29.0083333 180.489951 28.9656863 180.042157 28.8397059 179.595098 28.797549 179.281127 28.797549 178.833824 28.797549 178.251471 29.0083333 177.223039 29.5125 174.671814 30.8590686 172.478186 31.4485294 169.882353 31.4485294 166.302206 31.4485294 162.766176 30.270098 160.260049 28.2507353 156.72402 25.3897059 154.934559 21.3507353 154.934559 16.0919118 154.934559 10.3276961 157.0375 5.86838235 161.155147 2.79681373 163.571814.987990196 166.481127.0203431373 169.390686.0203431373 171.314706.0203431373 172.92598.357107843 174.940196 1.19803922 175.835294 1.57696078 176.282353 1.70294118 176.685294 1.70294118 177.088725 1.70294118 177.580392 1.61887255 178.251471 1.40808824 178.789461 1.19803922 178.968137 1.15612745 179.32598 1.15612745 179.728922 1.15612745 179.907843 1.40808824 179.907843 2.03970588" />
								</svg>
							</Link>
							<Navigation navigation={navigation} setup={setup} menuPopupHeight={this.state.menuPopupHeight} campaignMenu={header.campaignMenu} openMenuHandler={this.openMobileMenuHandler} resetFilterHandler={this.resetFilterHandler} />
							<div className={s.functions}>
								<div className={[s.functions_location, (this.state.location ? s.functions_location__open : null)].join(' ')} ref={this.languageCountryElement}>
									<a className={s.functions_location_title} href="#openLocation" onClick={this.openLocation}><span>{ setup.countries[setup.country].currency }</span><span>{ setup.language.toUpperCase() }</span></a>
									<div className={s.functions_location_dropdown}>
										<div className={s.functions_location_dropdown_languages}>
											<div className={s.functions_location_dropdown_languages_label}>{translate(setup, 'labelChooseLanguage')}</div>
											{
												Object.keys(setup.languages).map((lang) => (
													<Link key={lang} className={[s.functions_location_dropdown_language, (lang === setup.language ? s.functions_location_dropdown_language__selected : null)].join(' ')} to={`/${lang}/${(pagePaths && pagePaths[lang]) || ''}`} onClick={this.changeLanguage} data-language={lang}>
														{ setup.languages[lang].name }
													</Link>
												))
											}
										</div>
										<div className={s.functions_location_dropdown_country}>
											<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} label={translate(setup, 'labelShippingTo')} />
										</div>
										{ setup.unableToSendToCountry ? <span className={s.functions_location_dropdown_shipTo}>* {translate(setup, 'notShippingTo')}</span> : null }
									</div>
								</div>
								<Link className={s.functions_search} to={location.type === 'SEARCH' ? searchReturn : prefix(setup, 'search')}>
									<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
										<g fill="none" strokeLinecap="square" strokeWidth="1.5" transform="translate(3 11)">
											<circle cx="8.5" cy="8.5" r="8.5" />
											<line x1="14.936" x2="22.936" y1="14.936" y2="22.936" />
										</g>
									</svg>
								</Link>
								<Link className={s.functions_giftCard} to={prefix(setup, 'gift-card')}>
									<svg xmlns="http://www.w3.org/2000/svg" width="22" height="28" viewBox="0 0 22 28">
										<g fill="none" fillRule="evenodd" stroke="#000" strokeLinecap="square" strokeWidth="1.5" transform="translate(2 2)">
											<path d="M18.75,5.60742188 L18.75,25.1074219 L-0.75,25.1074219 L-0.75,5.60742188 L18.75,5.60742188 Z" />
											<line x1="9" x2="9" y1="24.857" y2="6.857" />
											<path d="M12,5.35742188 C13.6568542,5.35742188 16.239502,4.01427612 16.239502,2.35742188 C16.239502,0.700567626 15.0792175,-6.66133815e-15 13.4223633,-6.66133815e-15 C11.765509,-6.66133815e-15 10.5,1.41674805 10,2.35742188 C9.74544781,2.83632305 9.41211447,3.83632305 9,5.35742188 C10.4106221,5.35742188 11.4106221,5.35742188 12,5.35742188 Z" />
											<path d="M5,5.35742188 C6.65685425,5.35742188 9.23950195,4.01427612 9.23950195,2.35742188 C9.23950195,0.700567626 8.07921753,-6.66133815e-15 6.42236328,-6.66133815e-15 C4.76550903,-6.66133815e-15 3.5,1.41674805 3,2.35742188 C2.74544781,2.83632305 2.41211447,3.83632305 2,5.35742188 C3.4106221,5.35742188 4.4106221,5.35742188 5,5.35742188 Z" transform="matrix(-1 0 0 1 11.24 0)" />
											<path d="M10,6.35742187 C12.1043701,6.85620117 12.551886,9.29296875 14.2087402,9.29296875" transform="rotate(-180 12.104 7.825)" />
											<path d="M4,6.35742187 C6.10437012,6.85620117 6.55188598,9.29296875 8.20874023,9.29296875" transform="matrix(1 0 0 -1 0 15.65)" />
											<line x1=".5" x2="18.5" y1="11.857" y2="11.857" />
										</g>
									</svg>
								</Link>
								<Link className={s.functions_selection} to={prefix(setup, 'selection')} onClick={this.openCartHandler}>
									<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
										<g strokeLinecap="square" strokeWidth="1.5" transform="translate(5 5)">
											<polygon fill={(items && items.length > 0 ? '#A78E5C' : 'none')} points=".75 6 19.25 6 19.25 29.25 .75 29.25" />
											<path fill="none" d="M14.5,8 L14.5,4.60227273 C14.5,2.06050769 12.4852814,0 10,0 C7.51471863,0 5.5,2.06050769 5.5,4.60227273 L5.5,8" />
										</g>
									</svg>
								</Link>
								<div className={s.cart} ref={this.cartElement}>
									<div className={s.cart_inner}>
										<Cart maxHeight={this.state.menuPopupHeight} />
									</div>
								</div>
							</div>
						</div>
					</div>
				</header>
				{ location.type === 'SEARCH' && <div className={s.searchScreen} /> }
				<div className={[s.search, (location.type === 'SEARCH' ? s.search__active : null)].join(' ')}>
					{ location.type === 'SEARCH' && <Search /> }
				</div>
				{ grid ? <div className={s.grid}><div /></div> : null }
			</>
		);
	}
}

const mapState = ({ environment, checkout, location }) => {
	const { properties, navigation, setup, header, pageType, pagePaths } = environment;
	return { properties, navigation, setup, header, pageType, pagePaths, checkout, location };
};

export default hot(module)(connect(mapState, {
	removeNotification,
	openCart,
	openMenu,
	clearFilter,
	resetFilter,
	changeCountry,
	changeAddressInput,
	changeLanguage,
	goToPage,
	clientInit,
	changeDevice,
	setIframeHeight,
	cookieConsent,
})(Header));
